import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { SOCKET_BASE_URL } from '../config/api.js';
import { getCurrentCompanyId } from '../utils/tenantUtils.js';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

const resolveSocketIdentity = (user) => {
  const result = user?.result;
  if (!result) return null;

  const userTypeFromStorage = typeof window !== 'undefined' ? localStorage.getItem('userType') : null;
  const userType = userTypeFromStorage || result?.userType || 'company';
  const businessType = result.businessType || 'Home Appliance Technician';
  const userId = result._id;
  const companyId = userType === 'company' ? String(userId) : (result.companyId ? String(result.companyId) : null);

  return { userType, businessType, userId, companyId };
};

export const WebSocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const socketIdentity = useMemo(() => resolveSocketIdentity(user), [user]);

  useEffect(() => {
    if (!user?.result) return undefined;

    const newSocket = io(SOCKET_BASE_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
      timeout: 20000,
    });

    newSocket.on('connect', () => {
      setIsConnected(true);

      const identity = resolveSocketIdentity(user);
      if (!identity) return;

      newSocket.emit('join_room', {
        userType: identity.userType,
        userId: identity.userId,
        businessType: identity.businessType,
        companyId: identity.companyId
      });
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('🔌 WebSocket connection error:', error);
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [user]);

  const emitJobStatusUpdate = (jobData) => {
    if (socket && isConnected) {
      const identity = socketIdentity || resolveSocketIdentity(user);
      const companyId = identity?.companyId || getCurrentCompanyId();

      const payload = {
        ...jobData,
        userType: identity?.userType || 'worker',
        businessType: identity?.businessType || 'Home Appliance Technician',
        companyId,
        workerId: jobData.workerId || (identity?.userType === 'worker' ? identity?.userId : undefined),
        timestamp: new Date().toISOString()
      };

      socket.emit('job_status_update', payload);
    }
  };

  const emitWorkerStatusUpdate = (workerData) => {
    if (socket && isConnected) {
      const identity = socketIdentity || resolveSocketIdentity(user);
      const companyId = identity?.companyId || getCurrentCompanyId();

      socket.emit('worker_status_update', {
        ...workerData,
        businessType: identity?.businessType || 'Home Appliance Technician',
        companyId,
        timestamp: new Date().toISOString()
      });
    }
  };

  const value = {
    socket,
    isConnected,
    emitJobStatusUpdate,
    emitWorkerStatusUpdate
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
